import type { HTMLAttributes } from 'react';
import type React from 'react';
import type { TypeLinkDetailed } from 'types/autogen/contentful';
import styled from 'styled-components';
import { Typography } from 'components/generic/typography';
import InlineButton from 'components/generic/InlineButton';
import Flex from 'components/Layout/components/Flex';
import { ColorBrandBlue1, ColorNordicGrey4, Spacing4 } from 'autogen/design-tokens/tokens';
import { ReadAlso } from 'autogen/translation-keys/trans-website-articleblock';
import { useTranslation } from 'react-i18next';
import { backgroundStripes } from 'modules/helpers/style';
import { removeLinkTextDecoration, verticalEllipsis } from 'modules/helpers/mixins';
import Image from '../../Image/Image';
import WithAction from '../../shared/WithAction';

// #region Styles
const ImageContainer = styled.div`
    flex: 0 0 120px;
    aspect-ratio: 1;
    overflow: hidden;

    ${backgroundStripes({ color1: ColorBrandBlue1, color2: ColorNordicGrey4 })}

    & img {
        width: 100%;
        height: 100%;
        aspect-ratio: 1;
        object-fit: cover;
        object-position: center;
    }
`;

const Headline = styled(Typography.Headline)`
    ${verticalEllipsis(3)}
`;

const StyledWithAction = styled(WithAction)`
    ${removeLinkTextDecoration}
`;
// #endregion Styles

type RelatedArticleType = TypeLinkDetailed & {
    as?: React.ElementType | keyof JSX.IntrinsicElements;
} & HTMLAttributes<HTMLElement>;

export default function RelatedArticle(props: RelatedArticleType) {
    const { fields, metadata, sys, ...restProps } = props;
    const { imageRatio11, section, headline } = fields;
    const { t } = useTranslation();

    return (
        <StyledWithAction link={props} {...restProps}>
            <Flex.Row>
                <ImageContainer>{imageRatio11 && <Image {...imageRatio11} sizes="120px" />}</ImageContainer>
                <Flex.Column gap={Spacing4} alignItems="start">
                    {section && <Typography.Label size="S">{section.fields.publicName}</Typography.Label>}
                    <Headline size="S">{headline}</Headline>
                    <InlineButton as="span" fontWeight="semibold">
                        {t(ReadAlso)}
                    </InlineButton>
                </Flex.Column>
            </Flex.Row>
        </StyledWithAction>
    );
}
